const standardCowWeight = 700;
const standardCowFoodIntake = 0.02;

export const computeAGD = (DMperHA: number, areaInHA: number) =>
  Math.round(
    (DMperHA * areaInHA) / 10000 / (standardCowWeight * standardCowFoodIntake)
  );

export const minDryMatterSteps = [
  {
    lower: 0,
    upper: 1300,
    color: "#c37a63",
  },
  {
    lower: 1300,
    upper: 1400,
    color: "#d9dbbe",
  },
  {
    lower: 1400,
    upper: 1500,
    color: "#c4e2b6",
  },
  {
    lower: 1500,
    upper: 1600,
    color: "#a7d89b",
  },
  {
    lower: 1600,
    upper: 1700,
    color: "#85c973",
  },
  {
    lower: 1700,
    upper: 1800,
    color: "#6EA95F",
  },
  {
    lower: 1800,
    upper: 1900,
    color: "#4F8B3F",
  },
  {
    lower: 1900,
    upper: 2000,
    color: "#909231",
  },
  {
    lower: 2000,
    upper: 200000,
    color: "#909231",
  },
];

const hex2rgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

interface RGB {
  r: number;
  g: number;
  b: number;
}

// we dont necessarily care about perceived color distance. Euclidean Distance should be good enough
const colorDistance = (color1: RGB, color2: RGB) =>
  Math.sqrt(
    Math.pow(color1.r - color2.r, 2) +
      Math.pow(color1.g - color2.g, 2) +
      Math.pow(color1.b - color2.b, 2)
  );

export function colorToDM(color: RGB) {
  let index = 0;
  let min = Infinity;
  dryMatterNowSteps.forEach((s, idx) => {
    const matchScore = colorDistance(color, hex2rgb(s.color));
    if (min > matchScore) {
      min = matchScore;
      index = idx;
    }
  });
  return (dryMatterNowSteps[index].upper + dryMatterNowSteps[index].lower) / 2;
}

export function dryMatterToColor(
  dryMatter?: number,
  fallbackColor = "#ffffff"
) {
  return (
    dryMatterNowSteps.find(
      (d) => dryMatter && d.lower <= dryMatter && d.upper > dryMatter
    )?.color || fallbackColor
  );
}

// Leaf-stage zones (kg DM/ha → agronomic leaf stage). Single source of truth —
// used for the cover chips in the move dialog AND the grass-zone bar on the
// grazing records page, so a cover always reads as the same stage everywhere.
export type LeafZone = {
  min: number;
  max: number; // Infinity for the top zone
  name: string; // e.g. "Critical"
  leaf: string; // e.g. "L1" ("" for bare soil)
  label: string; // e.g. "Critical · L1"
  bg: string;
  tc: string;
  desc: string;
};

const LEAF_ZONE_BANDS: Array<Omit<LeafZone, "label">> = [
  {
    min: 0,
    max: 600,
    name: "Bare soil",
    leaf: "",
    bg: "#e5e7eb",
    tc: "#6b7280",
    desc: "No effective grass cover.",
  },
  {
    min: 600,
    max: 1200,
    name: "Critical",
    leaf: "L1",
    bg: "#fecaca",
    tc: "#b91c1c",
    desc: "1 leaf — severely overgrazed. Recovery will be slow.",
  },
  {
    min: 1200,
    max: 1600,
    name: "Residual",
    leaf: "L1–2",
    bg: "#FAF1B6",
    tc: "#A48137",
    desc: "Target post-grazing residual. Enough leaf area for fast regrowth.",
  },
  {
    min: 1600,
    max: 2200,
    name: "Regrowth",
    leaf: "L2",
    bg: "#DBF4C2",
    tc: "#2E7831",
    desc: "2 leaves — active regrowth phase, approaching optimal.",
  },
  {
    min: 2200,
    max: 2800,
    name: "Optimal",
    leaf: "L3",
    bg: "#AFE57F",
    tc: "#1C4321",
    desc: "3 leaves — ideal grazing window. Best balance of yield and quality.",
  },
  {
    min: 2800,
    max: Infinity,
    name: "Late",
    leaf: "L4+",
    bg: "#7EC254",
    tc: "#1C4321",
    desc: "4+ leaves — older leaves dying, quality declining.",
  },
];

export const LEAF_ZONES: LeafZone[] = LEAF_ZONE_BANDS.map((z) => ({
  ...z,
  label: z.leaf ? `${z.name} · ${z.leaf}` : z.name,
}));

/** Returns the leaf-stage zone for a cover value, or null when empty/zero. */
export function leafZoneForDryMatter(dryMatter?: number | ""): LeafZone | null {
  if (dryMatter === "" || dryMatter == null || dryMatter <= 0) return null;
  return (
    LEAF_ZONES.find((z) => dryMatter >= z.min && dryMatter < z.max) ?? null
  );
}

/**
 * Total dry matter utilised on a field during a grazing (kg DM), from its
 * pre- and post-grazing covers (kg DM/ha) and area (ha). Null if negative
 * (i.e. cover went up — not a valid grazing).
 */
export function dmUtilisedTotalKg(
  startCoverKgHa: number,
  endCoverKgHa: number,
  areaHa: number
): number | null {
  const perHa = startCoverKgHa - endCoverKgHa;
  if (perHa < 0) return null;
  return Math.round(perHa * areaHa);
}

// Cover sanity bounds (kg DM/ha): catch typos and impossible values.
export const COVER_MIN = 100;
export const COVER_MAX = 6000;

/**
 * Default residual to pre-fill "cover when leaving": the cover the field had
 * before grazing minus the herd's expected consumption over the grazing period
 * (daily feed demand kg DM/day × days ÷ area ha). Clamped so it never drops
 * below the post-grazing target (you graze down to it, not below) nor rises
 * above the cover before grazing. Falls back to the post-grazing target
 * (clamped to the cover before grazing) when the consumption inputs are missing.
 */
export function computeDefaultResidual({
  moveInCover,
  dailyDemand,
  daysGrazed,
  areaHa,
  target,
}: {
  moveInCover: number | null;
  dailyDemand: number;
  daysGrazed: number | null;
  areaHa: number | null;
  target: number | undefined;
}): number | "" {
  if (
    moveInCover != null &&
    dailyDemand > 0 &&
    daysGrazed != null &&
    areaHa != null &&
    areaHa > 0
  ) {
    const consumedPerHa = (dailyDemand * daysGrazed) / areaHa;
    const estimate = Math.round(moveInCover - consumedPerHa);
    const floor = target ?? COVER_MIN;
    return Math.min(moveInCover, Math.max(floor, estimate));
  }
  if (target == null) return "";
  if (moveInCover == null) return target;
  return Math.min(target, moveInCover);
}

export const dryMatterNowSteps = [
  {
    lower: 0,
    upper: 700,
    color: "#A8917B",
  },
  {
    lower: 700,
    upper: 1000,
    color: "#CABFA1",
  },
  {
    lower: 1000,
    upper: 1150,
    color: "#D6D6AD",
  },
  {
    lower: 1150,
    upper: 1300,
    color: "#D7E7AA",
  },
  {
    lower: 1300,
    upper: 1450,
    color: "#D1F094",
  },
  {
    lower: 1450,
    upper: 1600,
    color: "#C5E97C",
  },
  {
    lower: 1600,
    upper: 1750,
    color: "#B3E579",
  },
  {
    lower: 1750,
    upper: 1900,
    color: "#A5DE77",
  },
  {
    lower: 1900,
    upper: 2050,
    color: "#99D775",
  },
  {
    lower: 2050,
    upper: 2200,
    color: "#8ED173",
  },
  {
    lower: 2200,
    upper: 2350,
    color: "#82CA71",
  },
  {
    lower: 2350,
    upper: 2500,
    color: "#76C36F",
  },
  {
    lower: 2500,
    upper: 2650,
    color: "#6ABC6D",
  },
  {
    lower: 2650,
    upper: 2800,
    color: "#62B36C",
  },
  {
    lower: 2800,
    upper: 2950,
    color: "#59AB6C",
  },
  {
    lower: 2950,
    upper: 3100,
    color: "#52A36B",
  },
  {
    lower: 3100,
    upper: 3400,
    color: "#4A9A6A",
  },
  {
    lower: 3400,
    upper: 3700,
    color: "#41916A",
  },
  {
    lower: 3700,
    upper: 4000,
    color: "#388969",
  },
  {
    lower: 4000,
    upper: 4500,
    color: "#318168",
  },
  {
    lower: 4500,
    upper: 5000,
    color: "#1E7662",
  },
  {
    lower: 5000,
    upper: 6001,
    color: "#0E6E5C",
  },
];
